/**
 * Bitrix24 integration v1: webhook-only (connect via incoming webhook URL).
 */
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import { ComponentLoadingStore } from 'stores/componentLoadingStore';
import { IRootStore } from 'shared/entities/store/rootStore';
import NewListModel from 'shared/models/NewListModel';
import { LoadingStage } from 'shared/entities/meta';
import { apiUrls } from 'shared/entities/domain';
import { validateIsEmpty } from 'shared/entities/validator';
import { FieldModel } from 'shared/models/form';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { generateId } from 'shared/entities/common/utils';
import {
  Bitrix24AccountResponse,
  Bitrix24AccountsListResponse,
  IBitrix24NotConnectedAccount
} from 'shared/entities/bitrix24';
import { Bitrix24AccountModel } from 'shared/models/bitrix24';

const WEBHOOK_URL_PATTERN = /^https?:\/\/.+\/rest\/\d+\/.+\/$/i;

export default class Bitrix24Store extends ComponentLoadingStore {
  private rootStore: IRootStore;
  readonly accounts: NewListModel<Bitrix24AccountModel> =
    new NewListModel<Bitrix24AccountModel>();
  readonly notConnectedAccounts: NewListModel<IBitrix24NotConnectedAccount> =
    new NewListModel<IBitrix24NotConnectedAccount>();
  readonly displayName: FieldModel<string> = new FieldModel<string>('', [
    validateIsEmpty
  ]);
  readonly removingStage: LoadingStageModel = new LoadingStageModel();
  readonly saveWebhookLoadingStage: LoadingStageModel = new LoadingStageModel();
  private _saveWebhookError: string | null = null;
  private _removingAccountId: string | null = null;
  private _savingAccountId: string | null = null;

  constructor(rootStore: IRootStore) {
    super();
    this.rootStore = rootStore;

    makeObservable<
      Bitrix24Store,
      '_saveWebhookError' | '_removingAccountId' | '_savingAccountId'
    >(this, {
      _saveWebhookError: observable,
      _removingAccountId: observable,
      _savingAccountId: observable,
      itemsLength: computed,
      disabledAdding: computed,
      saveWebhookError: computed,
      getRemovingStage: action.bound,
      addNotConnectedAccount: action.bound,
      removeNotConnectedAccount: action.bound,
      saveAccount: action.bound,
      updateAccountWebhook: action.bound,
      removeAccount: action.bound,
      clearSaveWebhookError: action.bound
    });
  }

  getRemovingStage(accountId: string): LoadingStage {
    return this._removingAccountId === accountId
      ? this.removingStage.value
      : LoadingStage.NOT_STARTED;
  }

  getSavingStage(accountId: string): LoadingStage {
    return this._savingAccountId === accountId
      ? this.saveWebhookLoadingStage.value
      : LoadingStage.NOT_STARTED;
  }

  isSaving(accountId: string): boolean {
    return (
      this._savingAccountId === accountId &&
      this.saveWebhookLoadingStage.isLoading
    );
  }

  get saveWebhookError(): string | null {
    return this._saveWebhookError;
  }

  get itemsLength(): number {
    return this.accounts.length + this.notConnectedAccounts.length;
  }

  get disabledAdding(): boolean {
    const name = (this.displayName.value ?? '').trim();
    return !name || !!this.displayName.error;
  }

  get loaded(): boolean {
    return this.accounts.loadingStage.successfullyLoaded;
  }

  get canBeLoaded(): boolean {
    return this.rootStore.initialized;
  }

  clearSaveWebhookError(): void {
    this._saveWebhookError = null;
  }

  isValidWebhookUrl(url: string): boolean {
    const normalized = url.endsWith('/') ? url : `${url}/`;
    return WEBHOOK_URL_PATTERN.test(normalized);
  }

  addNotConnectedAccount(): void {
    this.displayName.validate();
    if (this.disabledAdding) {
      return;
    }

    const account: IBitrix24NotConnectedAccount = {
      id: generateId(),
      displayName: this.displayName.value.trim(),
      webhookUrl: new FieldModel<string>('', [validateIsEmpty])
    };

    this.notConnectedAccounts.addEntity({
      entity: account,
      key: account.id
    });

    this.displayName.changeValue('');
    this.displayName.resetError();
  }

  removeNotConnectedAccount(id: string): void {
    this.notConnectedAccounts.removeEntity(id);
  }

  async saveAccount(id: string): Promise<BaseResponse> {
    const account = this.notConnectedAccounts.getEntity(id);
    if (!account) {
      return { isError: true };
    }

    account.webhookUrl.validate();
    const url = account.webhookUrl.value.trim();
    if (!url || !this.isValidWebhookUrl(url)) {
      return { isError: true };
    }

    const normalizedUrl = url.endsWith('/') ? url : `${url}/`;

    this._savingAccountId = id;
    this.saveWebhookLoadingStage.loading();
    this.clearSaveWebhookError();

    const response =
      await this.rootStore.networkStore.api<Bitrix24AccountResponse>(
        apiUrls.BITRIX24_ACCOUNTS_ADD_WEBHOOK,
        {
          method: 'POST',
          data: {
            webhook_url: normalizedUrl,
            display_name: account.displayName
          }
        }
      );

    if (response.isError) {
      this.saveWebhookLoadingStage.error();
      this._saveWebhookError =
        (response as { errorMessage?: string }).errorMessage ?? 'Unknown error';
      this._savingAccountId = null;
      return { isError: true };
    }

    this.saveWebhookLoadingStage.success();

    const savedAccount = response.data?.account;
    if (savedAccount) {
      this.notConnectedAccounts.removeEntity(id);
      const entity = Bitrix24AccountModel.fromJson(savedAccount);
      this.accounts.addEntity({
        entity,
        key: entity.id,
        start: true
      });
    }

    this._savingAccountId = null;
    return { isError: false };
  }

  async updateAccountWebhook(
    accountId: string,
    webhookUrl: string
  ): Promise<BaseResponse> {
    const url = webhookUrl.trim();
    if (!url || !this.isValidWebhookUrl(url)) {
      return { isError: true };
    }

    const normalizedUrl = url.endsWith('/') ? url : `${url}/`;

    this._savingAccountId = accountId;
    this.saveWebhookLoadingStage.loading();
    this.clearSaveWebhookError();

    const response =
      await this.rootStore.networkStore.api<Bitrix24AccountResponse>(
        apiUrls.BITRIX24_ACCOUNTS_UPDATE_WEBHOOK,
        {
          method: 'POST',
          data: {
            _id: accountId,
            webhook_url: normalizedUrl
          }
        }
      );

    if (response.isError) {
      this.saveWebhookLoadingStage.error();
      this._saveWebhookError =
        (response as { errorMessage?: string }).errorMessage ?? 'Unknown error';
      this._savingAccountId = null;
      return { isError: true };
    }

    this.saveWebhookLoadingStage.success();
    this._savingAccountId = null;

    const updatedAccount = response.data?.account;
    const account = this.accounts.getEntity(accountId);
    if (account && updatedAccount) {
      runInAction(() => {
        account.mergeFromServer(updatedAccount);
      });
    }

    return { isError: false };
  }

  async removeAccount(id: string): Promise<BaseResponse> {
    this._removingAccountId = id;
    this.removingStage.loading();

    const response = await this.rootStore.networkStore.api(
      apiUrls.BITRIX24_ACCOUNTS_REMOVE,
      {
        method: 'POST',
        data: { _id: id }
      }
    );

    if (!response.isError) {
      this.accounts.removeEntity(id);
      this.removingStage.success();
    } else {
      this.removingStage.error();
    }

    setTimeout(() => {
      this._removingAccountId = null;
      this.removingStage.reset();
    }, 0);

    return { isError: response.isError };
  }

  async load(): Promise<BaseResponse> {
    if (this.accounts.loadingStage.isLoading) {
      return { isError: true };
    }

    this.accounts.loadingStage.loading();

    const response =
      await this.rootStore.networkStore.api<Bitrix24AccountsListResponse>(
        apiUrls.BITRIX24_ACCOUNTS_LIST
      );

    if (response.isError) {
      this.accounts.loadingStage.error();
    } else {
      const list = response.data?.accounts ?? [];
      this.accounts.fillByRawData({
        raw: list,
        normalizer: (rawItem) => {
          const entity = Bitrix24AccountModel.fromJson(rawItem);
          return {
            entity,
            key: entity.id
          };
        }
      });
      this.accounts.loadingStage.success();
    }

    return { isError: response.isError };
  }

  reset(): void {
    this.accounts.reset();
    this.notConnectedAccounts.reset();
    this.displayName.changeValue('');
    this.saveWebhookLoadingStage.reset();
    this.clearSaveWebhookError();
    this.removingStage.reset();
  }
}
